import type { GhostProps } from '../../rendering/AppearanceDefinitions';
import { GhostRenderer } from '../../rendering/GhostRenderer';
import type { GhostRenderOptions, HumanoidExtras } from '../../rendering/GhostRenderer';
import { gameLogger } from '../../../utils/GameLogger';
import { AppearanceHash } from '../../../shared/utils/AppearanceHash';

const COLORS = ['#ffffff', '#ffcccc', '#ccffcc', '#ccccff', '#ffffcc', '#e6e6fa', '#ffdab9', '#e0ffff'];
const EYES = ['round', 'happy', 'angry', 'winking'];
const MOUTHS = ['smile', 'o', 'neutral'];
const ACCESSORIES = ['none', 'bow', 'hat', 'crown'];
const WEAPON_TYPES = ['sword', 'staff', 'bow', 'axe'];
const WEAPON_COLORS = ['#C0C0C0', '#FFD700', '#8B4513', '#4682B4', '#CD5C5C'];
const ARMOR_TYPES = ['plate', 'robe', 'leather'];

const ARMOR_COLORS = ['#777', '#444', '#8B4513', '#333', '#555'];


export class TextureGenerator {
  private static readonly MAX_GHOST_CACHE_ENTRIES = 256;
  private static readonly ghostDataUrlCache = new Map<string, string>();

  static generateRandomAppearance(): GhostProps {
    gameLogger.debug('utils', '🎲 [proceduralghost] [TextureGenerator] Generating random appearance');
    return {
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      eyes: EYES[Math.floor(Math.random() * EYES.length)] as any,
      mouth: MOUTHS[Math.floor(Math.random() * MOUTHS.length)] as any,
      accessory: ACCESSORIES[Math.floor(Math.random() * ACCESSORIES.length)] as any,
      shape: 'classic',
      aura: 'none',
      weapon: {
        type: WEAPON_TYPES[Math.floor(Math.random() * WEAPON_TYPES.length)] as any,
        color: WEAPON_COLORS[Math.floor(Math.random() * WEAPON_COLORS.length)],
        accentColor: WEAPON_COLORS[Math.floor(Math.random() * WEAPON_COLORS.length)],
        variant: Math.floor(Math.random() * 3) + 1
      },
      equipment: {
        type: ARMOR_TYPES[Math.floor(Math.random() * ARMOR_TYPES.length)] as any,
        color: ARMOR_COLORS[Math.floor(Math.random() * ARMOR_COLORS.length)],
        helmet: 'none',
        slots: {
          head: 0, neck: 0, shoulders: 0, back: 0, chest: 0,
          hands: 0, waist: 0, legs: 0, feet: 0,
          mainHand: 0, offHand: 0, ranged: 0, classItem: 0
        }
      }
    };
  }

  static generateGhostSVG(
    props: GhostProps & { humanoid?: HumanoidExtras },
    options: GhostRenderOptions = {},
  ): string {
    // Pass the full appearance through — GhostRenderer handles defaults.
    // (Lane B 2026-07-01: the old hand-picked destructure silently DROPPED
    // `shape`, `faceMarking` and `blush`, which is why every preset-driven
    // character rendered with the same 'classic' silhouette. Now the
    // renderer honors everything the appearance declares, including the
    // optional `humanoid` extras layer used by Coil NPC portraits.)
    const sourceProps = typeof (props as GhostProps & { toJSON?: () => GhostProps }).toJSON === 'function'
      ? (props as GhostProps & { toJSON: () => GhostProps }).toJSON()
      : props;
    const { size = 256 /* High-res default for clean scaling */, className: _className, ...appearance } = sourceProps;
    const normalizedSize = Number.isFinite(size) ? Math.max(16, Math.min(1024, Math.round(size))) : 200;
    const pose = options.pose ?? 'idle';
    const idlePhase = options.idlePhase ?? 0;
    const cacheKey = `${AppearanceHash.generate(appearance as GhostProps)}:${normalizedSize}:${pose}${idlePhase ? `:${idlePhase}` : ''}`;
    const cached = TextureGenerator.ghostDataUrlCache.get(cacheKey);
    if (cached) return cached;

    const svgString = GhostRenderer.render(appearance, normalizedSize, { pose, idlePhase });

    const result = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
    if (TextureGenerator.ghostDataUrlCache.size >= TextureGenerator.MAX_GHOST_CACHE_ENTRIES) {
      const oldestKey = TextureGenerator.ghostDataUrlCache.keys().next().value as string | undefined;
      if (oldestKey) TextureGenerator.ghostDataUrlCache.delete(oldestKey);
    }
    TextureGenerator.ghostDataUrlCache.set(cacheKey, result);
    gameLogger.debug('utils', `🖼️ [proceduralghost] [TextureGenerator] Generated ghost SVG (length: ${result.length})`);
    return result;
  }

  static clearGhostCache(): void {
    TextureGenerator.ghostDataUrlCache.clear();
  }

  static getGhostCacheSize(): number {
    return TextureGenerator.ghostDataUrlCache.size;
  }

  static generatePalmTreeSVG(): string {
    const svg = `<svg width="100" height="150" viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
        <!-- Trunk -->
        <path d="M45 150 Q50 100 40 50 L60 50 Q50 100 55 150 Z" fill="#8B4513" />
        <path d="M40 50 L60 50 L50 150 Z" fill="none" stroke="#5D4037" stroke-width="2" opacity="0.5" />

        <!-- Leaves -->
        <g transform="translate(50, 50)">
            <path d="M0 0 Q-40 -20 -50 10" stroke="#228B22" stroke-width="8" fill="none" stroke-linecap="round" />
            <path d="M0 0 Q40 -20 50 10" stroke="#228B22" stroke-width="8" fill="none" stroke-linecap="round" />
            <path d="M0 0 Q-30 -40 -20 -50" stroke="#228B22" stroke-width="8" fill="none" stroke-linecap="round" />
            <path d="M0 0 Q30 -40 20 -50" stroke="#228B22" stroke-width="8" fill="none" stroke-linecap="round" />
            <path d="M0 0 Q0 -40 0 -60" stroke="#228B22" stroke-width="8" fill="none" stroke-linecap="round" />
        </g>

        <!-- Coconuts -->
        <circle cx="45" cy="55" r="5" fill="#3E2723" />
        <circle cx="55" cy="55" r="5" fill="#3E2723" />
        <circle cx="50" cy="60" r="5" fill="#3E2723" />
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  static generateShellSVG(): string {
    const svg = `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 25 Q15 5 25 25 Z" fill="#FFDAB9" stroke="#E9967A" stroke-width="1" />
        <path d="M5 25 Q15 15 25 25" fill="none" stroke="#E9967A" stroke-width="1" />
        <path d="M8 24 Q15 10 22 24" fill="none" stroke="#E9967A" stroke-width="1" />
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  static generateStarfishSVG(): string {
    const svg = `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
        <polygon points="15,2 19,11 28,11 21,17 24,26 15,21 6,26 9,17 2,11 11,11" fill="#FF6347" stroke="#CD5C5C" stroke-width="1" />
        <circle cx="13" cy="13" r="1" fill="#000" opacity="0.3" />
        <circle cx="17" cy="13" r="1" fill="#000" opacity="0.3" />
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  static generateTeleporterSVG(): string {
    const svg = `<svg width="100" height="40" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg">
        <!-- Outer Glow Ring -->
        <ellipse cx="50" cy="20" rx="45" ry="15" fill="none" stroke="#00FFFF" stroke-width="2" opacity="0.6">
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </ellipse>

        <!-- Inner Energy Field -->
        <ellipse cx="50" cy="20" rx="35" ry="10" fill="#E0FFFF" opacity="0.3">
            <animate attributeName="rx" values="35;38;35" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="ry" values="10;12;10" dur="1.5s" repeatCount="indefinite" />
        </ellipse>

        <!-- Core -->
        <ellipse cx="50" cy="20" rx="20" ry="5" fill="#FFFFFF" opacity="0.8">
             <animate attributeName="opacity" values="0.8;0.4;0.8" dur="0.5s" repeatCount="indefinite" />
        </ellipse>

        <!-- Floating Particles -->
        <circle cx="30" cy="20" r="1" fill="#FFF">
            <animate attributeName="cy" values="20;10;20" dur="1s" repeatCount="indefinite" />
        </circle>
        <circle cx="70" cy="20" r="1" fill="#FFF">
            <animate attributeName="cy" values="20;10;20" dur="1.2s" repeatCount="indefinite" />
        </circle>
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  static generatePigSVG(): string {
    const svg = `<svg width="60" height="40" viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
      <!-- Body -->
      <ellipse cx="30" cy="20" rx="25" ry="15" fill="#FFC0CB" stroke="#FF69B4" stroke-width="2" />
      <!-- Snout -->
      <ellipse cx="10" cy="20" rx="5" ry="4" fill="#FF69B4" />
      <circle cx="8" cy="19" r="1" fill="black" />
      <circle cx="8" cy="21" r="1" fill="black" />
      <!-- Eye -->
      <circle cx="18" cy="15" r="2" fill="white" />
      <circle cx="19" cy="15" r="1" fill="black" />
      <!-- Ears -->
      <path d="M25 8 L30 2 L35 8 Z" fill="#FFC0CB" stroke="#FF69B4" stroke-width="2" />
      <!-- Tail -->
      <path d="M55 20 Q60 15 55 10" fill="none" stroke="#FF69B4" stroke-width="2" />
      <!-- Legs -->
      <rect x="20" y="30" width="4" height="8" fill="#FFC0CB" stroke="#FF69B4" />
      <rect x="40" y="30" width="4" height="8" fill="#FFC0CB" stroke="#FF69B4" />
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  static generateTrainingDummySVG(): string {
    const svg = `<svg width="50" height="80" viewBox="0 0 50 80" xmlns="http://www.w3.org/2000/svg">
      <!-- Stand -->
      <rect x="20" y="60" width="10" height="20" fill="#8B4513" />
      <rect x="5" y="75" width="40" height="5" fill="#8B4513" />
      <!-- Body -->
      <rect x="10" y="20" width="30" height="40" fill="#D2B48C" stroke="#8B4513" stroke-width="2" />
      <!-- Target Circle -->
      <circle cx="25" cy="40" r="10" fill="#FF0000" opacity="0.5" />
      <circle cx="25" cy="40" r="5" fill="#FF0000" />
      <!-- Head -->
      <circle cx="25" cy="15" r="10" fill="#D2B48C" stroke="#8B4513" stroke-width="2" />
      <!-- Arms -->
      <rect x="0" y="30" width="50" height="5" fill="#8B4513" />
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }
}
